import { db, getOrCreateSettings } from '../db/db.js';
import type { Item, ShoppingItem } from '../db/schema.js';
import type { Category } from '../events/types.js';
import { aggregateItemStatus } from './expiry.js';
import { normalizeName } from './normalize.js';
import { dueSubscriptionsForSuggestion } from './subscription.js';
import { lookupKnowledge } from './knowledge.js';

export type SuggestionReason = 'expiring_soon' | 'low_stock' | 'subscription_due';

export interface Suggestion {
  reason: SuggestionReason;
  /** UI x-for unique key（itemId 或 `sub:${subscriptionId}`） */
  key: string;
  /** 庫存型建議才有；subscription_due 時 undefined */
  itemId?: string;
  /** subscription_due 時必填；用於 addShoppingItem 帶 subscriptionId 連回 */
  subscriptionId?: string;
  name: string;
  category: Category;
  detail: string;
  rankScore: number;
  suggestedQuantity?: number;
  suggestedUnit?: string;
}

const MAX_SUGGESTIONS = 8;

/**
 * 「家裡缺什麼」自動偵測（v0.2：限定 minQuantity > 0 的常備品才建議）。
 *
 * 觸發訊號：
 *   - low_stock：常備品 (minQuantity > 0) 且 totalQuantity < minQuantity
 *   - expiring_soon：常備品 + active item 有快過期 batch
 *
 * 非常備品（minQuantity 未設或 0）一律不建議補貨 — 一次性食材吃完就算。
 *
 * v0.2+：subscription_due / recipe_suggestion (v0.3 LLM 推薦)。
 */
export async function loadSuggestions(now: number = Date.now()): Promise<Suggestion[]> {
  const settings = await getOrCreateSettings();
  const items = await db.items.toArray();
  const shoppingActive = await db.shoppingItems.toArray();

  const dedupSet = buildShoppingDedupSet(shoppingActive);

  const suggestions: Suggestion[] = [];

  for (const item of items) {
    if (dedupSet.has(item.normalizedName)) continue;

    // v0.2 篩選：只看設過常備需求量的 item
    const minQty = item.minQuantity ?? 0;
    if (minQty <= 0) continue;

    const totalQuantity = item.batches.reduce((s, b) => s + b.quantity, 0);

    // 庫存不足（含全消耗）→ low_stock
    if (totalQuantity < minQty) {
      const shortfall = minQty - totalQuantity;
      const unit = item.batches[0]?.unit;
      const detail =
        totalQuantity === 0
          ? `已用完（常備需求 ${minQty}${unit ?? ''}）`
          : `剩 ${totalQuantity}${unit ?? ''}（常備需求 ${minQty}${unit ?? ''}）`;
      const out: Suggestion = {
        reason: 'low_stock',
        key: item.id,
        itemId: item.id,
        name: item.name,
        category: item.category,
        detail,
        rankScore: 50 + shortfall, // 缺得越多排越前
        suggestedQuantity: shortfall,
      };
      if (unit) out.suggestedUnit = unit;
      suggestions.push(out);
      continue;
    }

    // 庫存足但有 batch 快過期 → expiring_soon（提醒換新批）
    if (item.status === 'active') {
      const expiry = aggregateItemStatus(item.batches, settings.expiryWarningDays, now);
      if (expiry.status === 'expired' || expiry.status === 'warning') {
        const detail =
          expiry.status === 'expired'
            ? `已過期 ${Math.abs(expiry.daysLeft ?? 0)} 天`
            : expiry.daysLeft === 0
            ? '今天到期'
            : `${expiry.daysLeft} 天到期`;
        const rankScore =
          (expiry.status === 'expired' ? 1000 : 100) - (expiry.daysLeft ?? 0);
        const out: Suggestion = {
          reason: 'expiring_soon',
          key: item.id,
          itemId: item.id,
          name: item.name,
          category: item.category,
          detail,
          rankScore,
        };
        if (totalQuantity > 0) out.suggestedQuantity = totalQuantity;
        const unit = item.batches[0]?.unit;
        if (unit) out.suggestedUnit = unit;
        suggestions.push(out);
      }
    }
  }

  // v0.4 D：subscription due 建議
  const dueSubs = await dueSubscriptionsForSuggestion(now);
  for (const sub of dueSubs) {
    if (dedupSet.has(normalizeName(sub.defaultName))) continue;
    const knowledge = await lookupKnowledge(sub.defaultName);
    const category: Category = knowledge?.category ?? 'other';
    const daysUntil = Math.ceil((sub.nextDelivery - now) / 86_400_000);
    const detail =
      daysUntil <= 0
        ? `今天到貨（訂閱：${sub.source}）`
        : `${daysUntil} 天後到貨（訂閱：${sub.source}）`;
    const out: Suggestion = {
      reason: 'subscription_due',
      key: `sub:${sub.id}`,
      subscriptionId: sub.id,
      name: sub.defaultName,
      category,
      detail,
      rankScore: 200 - daysUntil, // 越接近到貨日越前；rank 在 low_stock 之上、expired 之下
      suggestedQuantity: sub.defaultQuantity,
      suggestedUnit: sub.defaultUnit,
    };
    suggestions.push(out);
  }

  suggestions.sort((a, b) => b.rankScore - a.rankScore);
  return suggestions.slice(0, MAX_SUGGESTIONS);
}

function buildShoppingDedupSet(rows: ShoppingItem[]): Set<string> {
  const set = new Set<string>();
  for (const row of rows) {
    // v0.4 in_transit 也算進行中，不再 suggest 同名
    if (row.status === 'planned' || row.status === 'ordered' || row.status === 'in_transit') {
      set.add(normalizeName(row.name));
    }
  }
  return set;
}

export type { Item };
